import { cldImg } from "../../../lib/cloudinary";
import { useEffect, useState } from "react";
import axios from "axios";
import Header from "../../../components/Header";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, CalendarDays, Moon, Users, ChevronRight, AlertCircle, Clock, CheckCircle2, XCircle, Ban, Star, CalendarRange } from "lucide-react";
import ReviewForm from "./ReviewForm";

const G       = "var(--k-green)";
const G_DARK  = "var(--k-green-hover)";
const G_LIGHT = "var(--k-green-soft)";
const API     = "https://api.komorotel.com";

const fmtDate = d => new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
const nights  = (ci, co) => Math.ceil((new Date(co) - new Date(ci)) / 86400000);

// 🆕 VERSEMENT — échéance de règlement : la date seule ne suffit pas,
// l'heure fait partie du délai.
const fmtDeadline = d => new Date(d).toLocaleString("fr-FR", {
  day: "numeric", month: "long", hour: "2-digit", minute: "2-digit",
});

/* 🆕 PÉRIODES — durée dans l'unité réservée.
   "2 mois" plutôt que "60 nuits" : c'est ce que le client a choisi. */
const UNIT_LABEL = { 7: "semaine", 30: "mois" };

function stayLabel(r) {
  if (r.periods && r.unitNights) {
    const l = UNIT_LABEL[r.unitNights] || "période";
    const plural = l === "mois" ? l : (r.periods > 1 ? `${l}s` : l);
    return `${r.periods} ${plural}`;
  }
  const n = nights(r.checkInDate, r.checkOutDate);
  return `${n} nuit${n > 1 ? "s" : ""}`;
}

/* ── Statuts ── */
const STATUS_MAP = {
  confirmed:      { label: "Confirmée",            icon: <CheckCircle2 size={13} />, bg: "var(--k-green-soft)", color: G,        dot: G        },
  completed:      { label: "Terminée",             icon: <CheckCircle2 size={13} />, bg: "#f9fafb", color: "#6b7280", dot: "#9ca3af" },
  pending_cancel: { label: "Annulation en cours",  icon: <Clock size={13} />,        bg: "#fffbeb", color: "#d97706", dot: "#f59e0b" },
  cancelled:      { label: "Annulée",              icon: <XCircle size={13} />,      bg: "#fef2f2", color: "#dc2626", dot: "#ef4444" },
  pending:        { label: "En attente",           icon: <Clock size={13} />,        bg: "#eff6ff", color: "#2563eb", dot: "#3b82f6" },
  // 🆕 VERSEMENT — les dates sont bloquées mais le séjour n'est pas acquis.
  // Le libellé le dit sans ambiguïté : "En attente" seul laisserait croire
  // que le client n'a rien à faire.
  awaiting_deposit: { label: "Règlement à faire",  icon: <Clock size={13} />,        bg: "#fffbeb", color: "#d97706", dot: "#f59e0b" },
};

/* ── Séjour badge ── */
const StayBadge = ({ isUpcoming, isOngoing, isCompleted }) => {
  if (isOngoing)   return <span style={{ fontSize: 11, fontWeight: 700, color: G,        background: G_LIGHT,  padding: "3px 10px", borderRadius: 99 }}>En cours</span>;
  if (isUpcoming)  return <span style={{ fontSize: 11, fontWeight: 700, color: "#2563eb", background: "#eff6ff", padding: "3px 10px", borderRadius: 99 }}>À venir</span>;
  if (isCompleted) return <span style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", background: "#f3f4f6", padding: "3px 10px", borderRadius: 99 }}>Terminé</span>;
  return null;
};

/* ── Étoiles lecture seule ── */
const Stars = ({ rating }) => (
  <div style={{ display: "flex", gap: 2 }}>
    {[1,2,3,4,5].map(s => (
      <Star key={s} size={12} fill={s <= rating ? "#f59e0b" : "none"} color={s <= rating ? "#f59e0b" : "#d1d5db"} />
    ))}
  </div>
);

export default function MyReservations() {
  const [reservations,  setReservations]  = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [openReviewId,  setOpenReviewId]  = useState(null);
  const [editReviewId,  setEditReviewId]  = useState(null);
  const [expanded,      setExpanded]      = useState(null); // détail déployé

  useEffect(() => {
    axios.get(`${API}/api/reservation/user`, { withCredentials: true })
      .then(r => setReservations(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleCancelRequest = async (id) => {
    if (!window.confirm("Confirmer la demande d'annulation ?")) return;
    try {
      const res = await axios.post(`${API}/api/reservation/${id}/cancellation-request`, { reservationId: id }, { withCredentials: true });
      // 🆕 VERSEMENT — annuler avant paiement est immédiat et sans frais :
      // le serveur renvoie auto:true et la résa passe directement en annulée.
      const newStatus = res.data?.auto ? "cancelled" : "pending_cancel";
      setReservations(prev => prev.map(r => r._id === id ? { ...r, status: newStatus } : r));
    } catch (err) { alert(err.response?.data?.message || "Erreur"); }
  };

  const handleReviewSuccess = (reservationId) => {
    setOpenReviewId(null);
    setReservations(prev => prev.map(r => r._id === reservationId ? { ...r, review: { submitted: true } } : r));
  };

  const handleEditSuccess = (reservationId, { rating, comment }) => {
    setEditReviewId(null);
    setReservations(prev => prev.map(r =>
      r._id === reservationId ? { ...r, review: { ...r.review, rating, comment } } : r
    ));
  };

  // 🆕 PÉRIODES — formatte un délai stocké en heures, en jours sur un séjour
  // forfaitaire (weekly / monthly), en heures sinon.
  const formatDelay = (hours, isLongStay) => {
    if (isLongStay) {
      const d = Math.round(hours / 24);
      return `${d} jour${d > 1 ? "s" : ""}`;
    }
    return `${hours}h`;
  };

  const getPolicy = (r) => {
    const policy   = r.ratePlanSnapshot?.cancellationPolicy;
    // 🆕 PÉRIODES — les valeurs sont weekly / monthly depuis la migration.
    // "long_stay" reste possible sur des réservations plus anciennes.
    const stayType = r.ratePlanSnapshot?.stayType;
    const isLongStay = Boolean(stayType) && stayType !== "short_stay";
    const checkIn  = new Date(r.checkInDate); checkIn.setHours(0,0,0,0);
    const diffHours = (checkIn.getTime() - new Date().getTime()) / 3600000;
    if (!policy) return { text: "Conditions à confirmer avec l'hôtel", color: "#9ca3af", refundable: true };
    if (policy.type === "free") {
      const free = diffHours >= policy.hoursBeforeArrival;
      return { text: `Annulation gratuite jusqu'à ${formatDelay(policy.hoursBeforeArrival, isLongStay)} avant`, refund: free ? "Remboursement total" : "Non remboursable maintenant", color: free ? G : "#ef4444", refundable: free };
    }
    if (policy.type === "partial") {
      // 🆕 FRAIS FIXES — fallback "percentage" pour compat avec d'anciens plans sans penaltyMode
      const mode = policy.penaltyMode || "percentage";
      const isFixed = mode === "fixed";
      const withinDeadline = diffHours >= policy.hoursBeforeArrival;
      const feeLabel = isFixed ? `${policy.penaltyFixedAmount}€` : `${policy.penaltyPercentage}%`;
      const refund = withinDeadline
        ? "Remboursement total"
        : isFixed
          ? `Remboursement du solde (- ${policy.penaltyFixedAmount}€)`
          : `Remboursement ${100 - policy.penaltyPercentage}%`;
      return { text: `Frais ${feeLabel} après ${formatDelay(policy.hoursBeforeArrival, isLongStay)}`, refund, color: "#d97706", refundable: true };
    }
    return { text: "Non remboursable", refund: "Aucun remboursement", color: "#ef4444", refundable: false };
  };

  /* ── Skeleton ── */
  if (loading) return (
    <>
      <Header />
      <div style={{ minHeight: "100vh", background: "#fafaf9", paddingTop: 100, paddingBottom: 80, fontFamily: "'DM Sans',system-ui,sans-serif" }}>
        <style>{`@keyframes shimmer{to{background-position:-200% 0}}`}</style>
        <div style={{ maxWidth: 840, margin: "0 auto", padding: "0 24px" }}>
          <div style={{ height: 32, width: 220, borderRadius: 8, marginBottom: 8, background: "linear-gradient(90deg,#f3f4f6 25%,#e9eaec 50%,#f3f4f6 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite" }} />
          <div style={{ height: 16, width: 160, borderRadius: 8, marginBottom: 40, background: "linear-gradient(90deg,#f3f4f6 25%,#e9eaec 50%,#f3f4f6 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite" }} />
          {[260, 240, 260].map((h, i) => (
            <div key={i} style={{ height: h, borderRadius: 20, marginBottom: 20, background: "linear-gradient(90deg,#f3f4f6 25%,#e9eaec 50%,#f3f4f6 75%)", backgroundSize: "200% 100%", animation: `shimmer 1.4s ${i * 0.15}s infinite` }} />
          ))}
        </div>
      </div>
    </>
  );

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400&family=Playfair+Display:wght@600;700&display=swap');
        @keyframes shimmer { to { background-position: -200% 0; } }
        @keyframes fadeUp  { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
        @keyframes pulse   { 0%,100% { opacity:1; } 50% { opacity:0.5; } }

        .resa-card {
          background: #fff;
          border-radius: 20px;
          border: 1px solid #efefef;
          overflow: hidden;
          transition: box-shadow 0.25s, transform 0.25s;
          cursor: default;
        }
        .resa-card:hover { box-shadow: 0 12px 40px rgba(0,0,0,0.09); transform: translateY(-2px); }

        /* 🆕 VERSEMENT — une réservation en attente de règlement demande une
           action du client. Le liseré la sort de la liste sans crier. */
        .resa-card.awaiting { border-color: #fde68a; }

        .resa-img-wrap {
          position: relative;
          width: 220px;
          min-height: 220px;
          flex-shrink: 0;
          overflow: hidden;
        }
        .resa-img-wrap img {
          width: 100%; height: 100%; object-fit: cover;
          transition: transform 0.6s cubic-bezier(0.25,0.46,0.45,0.94);
        }
        .resa-card:hover .resa-img-wrap img { transform: scale(1.06); }

        .resa-cancel-btn {
          font-size: 12px; font-weight: 600; color: #ef4444; background: none; border: none;
          cursor: pointer; padding: 0; transition: color 0.15s;
        }
        .resa-cancel-btn:hover { color: #dc2626; }

        .review-btn {
          display: inline-flex; align-items: center; gap: 6px;
          font-size: 13px; font-weight: 600; color: ${G};
          background: ${G_LIGHT}; border: 1.5px solid rgb(var(--k-green-rgb)/0.19);
          border-radius: 10px; padding: 8px 14px; cursor: pointer;
          transition: all 0.18s;
        }
        .review-btn:hover { background: rgb(var(--k-green-rgb)/0.09); border-color: rgb(var(--k-green-rgb)/0.38); }

        /* Mobile */
        @media (max-width: 640px) {
          .resa-img-wrap { width: 100% !important; min-height: 180px !important; }
          .resa-body-flex { flex-direction: column !important; }
          .resa-bottom-row { flex-direction: column !important; align-items: flex-start !important; gap: 12px !important; }
        }
      `}</style>

      <Header />
      <div style={{ minHeight: "100vh", background: "#fafaf9", fontFamily: "'DM Sans',system-ui,sans-serif", paddingTop: 96, paddingBottom: 80 }}>
        <div style={{ maxWidth: 840, margin: "0 auto", padding: "0 24px" }}>

          {/* ── En-tête ── */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} style={{ marginBottom: 40 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
              <div style={{ width: 4, height: 32, borderRadius: 99, background: G }} />
              <h1 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "clamp(24px,5vw,32px)", fontWeight: 700, color: "#111827", margin: 0 }}>
                Mes réservations
              </h1>
            </div>
            <p style={{ fontSize: 14, color: "#9ca3af", marginLeft: 16, fontStyle: "italic" }}>
              {reservations.length} séjour{reservations.length !== 1 ? "s" : ""} · Consultez et gérez vos voyages
            </p>
          </motion.div>

          {/* ── Vide ── */}
          {reservations.length === 0 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              style={{ background: "#fff", borderRadius: 20, border: "1px dashed #e5e7eb", padding: "64px 24px", textAlign: "center" }}>
              <div style={{ width: 64, height: 64, background: G_LIGHT, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                <CalendarDays size={28} color={G} />
              </div>
              <p style={{ fontSize: 16, fontWeight: 600, color: "#374151", marginBottom: 8 }}>Aucune réservation</p>
              <p style={{ fontSize: 14, color: "#9ca3af" }}>Vos prochains séjours aux Comores apparaîtront ici.</p>
            </motion.div>
          )}

          {/* ── Liste ── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {reservations.map((r, i) => {
              const today    = new Date(); today.setHours(0,0,0,0);
              const checkIn  = new Date(r.checkInDate);  checkIn.setHours(0,0,0,0);
              const checkOut = new Date(r.checkOutDate); checkOut.setHours(0,0,0,0);
              const n        = nights(r.checkInDate, r.checkOutDate);

              const isUpcoming  = today < checkIn;
              const isOngoing   = today >= checkIn && today < checkOut;
              const isCompleted = r.status === "completed";

              // 🆕 VERSEMENT
              const awaitingDeposit = r.status === "awaiting_deposit";
              // 🆕 PÉRIODES
              const isPeriodStay = Boolean(r.periods && r.unitNights);

              const policyInfo = getPolicy(r);
              const statusUI   = STATUS_MAP[r.status] || STATUS_MAP.pending;

              // 🆕 VERSEMENT — annuler avant paiement est toujours possible et
              // sans frais : rien n'a été encaissé.
              const canCancel       = (r.status === "confirmed" && isUpcoming && policyInfo.refundable)
                                      || (awaitingDeposit && isUpcoming);
              const canReview       = isCompleted && !r.review?.submitted;
              const alreadyReviewed = isCompleted && r.review?.submitted;
              const canEdit         = alreadyReviewed && r.review?.reviewId?._id && r.review?.reviewId?.status === "pending";

              const isReviewOpen = openReviewId === r._id;
              const isEditOpen   = editReviewId === r._id;

              return (
                <motion.div key={r._id}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.07, duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
                  className={`resa-card${awaitingDeposit ? " awaiting" : ""}`}>

                  {/* 🆕 VERSEMENT — bandeau d'action, en tête de carte.
                      Placé avant la photo : c'est l'information qui doit être
                      lue en premier, pas le décor. */}
                  {awaitingDeposit && r.deposit && (
                    <div style={{ background: "#fffbeb", borderBottom: "1px solid #fde68a", padding: "12px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <Clock size={16} color="#d97706" style={{ flexShrink: 0 }} />
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: "#92400e" }}>
                            {r.deposit.amount}€ à régler auprès de {r.hotelId?.name}
                          </div>
                          {r.deposit.dueAt && (
                            <div style={{ fontSize: 11, color: "#a16207", marginTop: 2 }}>
                              Avant le {fmtDeadline(r.deposit.dueAt)}, sinon les dates repartent à la vente
                            </div>
                          )}
                        </div>
                      </div>
                      <span style={{ fontSize: 11, color: "#a16207", fontFamily: "monospace", whiteSpace: "nowrap" }}>
                        Réf · {r.reservationId}
                      </span>
                    </div>
                  )}

                  {/* ── Corps principal ── */}
                  <div className="resa-body-flex" style={{ display: "flex" }}>

                    {/* Image */}
                    <div className="resa-img-wrap">
                      <img src={cldImg(r.roomId?.photos?.[0], 640)} alt={r.roomId?.title} loading="lazy" />
                      {/* Gradient overlay bas */}
                      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 80, background: "linear-gradient(transparent, rgba(0,0,0,0.45))" }} />
                      {/* Badge séjour sur image */}
                      <div style={{ position: "absolute", top: 12, left: 12 }}>
                        <StayBadge isUpcoming={isUpcoming} isOngoing={isOngoing} isCompleted={isCompleted} />
                      </div>
                      {/* 🆕 PÉRIODES — durée dans l'unité réservée */}
                      <div style={{ position: "absolute", bottom: 12, left: 12, display: "flex", alignItems: "center", gap: 5 }}>
                        {isPeriodStay
                          ? <CalendarRange size={12} color="rgba(255,255,255,0.85)" />
                          : <Moon size={12} color="rgba(255,255,255,0.85)" />}
                        <span style={{ fontSize: 12, fontWeight: 600, color: "#fff" }}>{stayLabel(r)}</span>
                      </div>
                    </div>

                    {/* Infos */}
                    <div style={{ flex: 1, padding: "22px 24px", display: "flex", flexDirection: "column", justifyContent: "space-between", minWidth: 0 }}>

                      {/* Top */}
                      <div>
                        {/* Hôtel + ville */}
                        <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#9ca3af", marginBottom: 6 }}>
                          <MapPin size={12} color="#9ca3af" />
                          <span>{r.hotelId?.name}{r.hotelId?.city ? ` · ${r.hotelId.city}` : ""}</span>
                        </div>

                        {/* Titre chambre */}
                        <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "clamp(15px, 3vw, 18px)", fontWeight: 700, color: "#111827", margin: "0 0 12px", lineHeight: 1.3 }}>
                          {r.roomId?.title}
                        </h2>

                        {/* Dates */}
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#4b5563", background: "#f9fafb", borderRadius: 8, padding: "5px 10px" }}>
                            <CalendarDays size={13} color="#9ca3af" />
                            <span>{fmtDate(r.checkInDate)}</span>
                            <span style={{ color: "#d1d5db" }}>→</span>
                            <span>{fmtDate(r.checkOutDate)}</span>
                          </div>
                          {/* 🆕 PÉRIODES — le nombre de nuits reste utile en second */}
                          {isPeriodStay && (
                            <span style={{ fontSize: 12, color: "#9ca3af" }}>{n} nuits</span>
                          )}
                        </div>

                        {/* Réf */}
                        <p style={{ fontSize: 11, color: "#d1d5db", fontFamily: "monospace", letterSpacing: "0.05em", marginBottom: 10 }}>
                          Réf · {r.reservationId}
                        </p>

                        {/* Politique annulation */}
                        <div style={{ display: "flex", alignItems: "flex-start", gap: 6, marginTop: 2 }}>
                          <div style={{ width: 6, height: 6, borderRadius: "50%", background: policyInfo.color, marginTop: 5, flexShrink: 0 }} />
                          <div>
                            <p style={{ fontSize: 12, color: policyInfo.color, fontWeight: 500, margin: 0 }}>{policyInfo.text}</p>
                            {policyInfo.refund && <p style={{ fontSize: 11, color: "#9ca3af", margin: "2px 0 0" }}>{policyInfo.refund}</p>}
                          </div>
                        </div>
                      </div>

                      {/* ── Bottom row ── */}
                      <div className="resa-bottom-row" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 20, flexWrap: "wrap", gap: 12 }}>

                        {/* Statut badge */}
                        <div style={{
                          display: "inline-flex", alignItems: "center", gap: 6,
                          background: statusUI.bg, color: statusUI.color,
                          fontSize: 12, fontWeight: 700, borderRadius: 99,
                          padding: "5px 12px",
                        }}>
                          {statusUI.icon}
                          {statusUI.label}
                        </div>

                        {/* Prix + actions */}
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 22, fontWeight: 700, color: "#111827", lineHeight: 1 }}>
                            {r.totalPrice}€
                          </div>
                          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>Total séjour</div>

                          {canCancel && (
                            <button className="resa-cancel-btn" style={{ marginTop: 8, display: "block" }}
                              onClick={() => handleCancelRequest(r._id)}>
                              {/* 🆕 VERSEMENT — rien n'est encaissé, le mot "annuler" suffit */}
                              {awaitingDeposit ? "Annuler cette réservation" : "Demander l'annulation"}
                            </button>
                          )}
                          {!canCancel && r.status === "confirmed" && (
                            <p style={{ fontSize: 11, color: "#d1d5db", marginTop: 6 }}>Annulation non disponible</p>
                          )}
                          {r.status === "pending_cancel" && (
                            <p style={{ fontSize: 11, color: "#f59e0b", marginTop: 6, animation: "pulse 2s infinite" }}>⏳ Validation en attente</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ── Zone avis (pied de card) ── */}
                  <AnimatePresence>
                    {(canReview || alreadyReviewed || isReviewOpen || isEditOpen) && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        style={{ borderTop: "1px solid #f3f4f6", padding: "16px 24px", background: "#fafaf9" }}
                      >
                        {/* Avis soumis */}
                        {alreadyReviewed && !isEditOpen && (
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <div style={{ width: 28, height: 28, borderRadius: "50%", background: G_LIGHT, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <CheckCircle2 size={14} color={G} />
                              </div>
                              <div>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <span style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Avis soumis</span>
                                  {r.review?.reviewId?.rating && <Stars rating={r.review.reviewId.rating} />}
                                </div>
                                <span style={{ fontSize: 11, color: r.review?.reviewId?.status === "approved" ? G : "#f59e0b" }}>
                                  {r.review?.reviewId?.status === "approved" ? "✓ Publié" : "⏳ En attente de validation"}
                                </span>
                              </div>
                            </div>
                            {canEdit && (
                              <button
                                onClick={() => setEditReviewId(r._id)}
                                style={{ fontSize: 12, fontWeight: 600, color: G, background: "none", border: `1px solid rgb(var(--k-green-rgb)/0.25)`, borderRadius: 8, padding: "5px 12px", cursor: "pointer", transition: "all 0.15s" }}
                                onMouseEnter={e => { e.currentTarget.style.background = G_LIGHT; }}
                                onMouseLeave={e => { e.currentTarget.style.background = "none"; }}>
                                Modifier
                              </button>
                            )}
                          </div>
                        )}

                        {/* Form modification */}
                        {isEditOpen && (
                          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                            <ReviewForm
                              mode="edit"
                              hotelName={r.hotelId?.name}
                              reviewId={r.review?.reviewId?._id}
                              initialRating={r.review?.reviewId?.rating || 0}
                              initialComment={r.review?.reviewId?.comment || ""}
                              onSuccess={(data) => handleEditSuccess(r._id, data)}
                            />
                            <button onClick={() => setEditReviewId(null)}
                              style={{ marginTop: 10, fontSize: 12, color: "#9ca3af", background: "none", border: "none", cursor: "pointer" }}>
                              Annuler
                            </button>
                          </motion.div>
                        )}

                        {/* Nouveau avis */}
                        {canReview && !isReviewOpen && (
                          <button className="review-btn" onClick={() => setOpenReviewId(r._id)}>
                            <Star size={14} fill={G} color={G} />
                            Laisser un avis
                          </button>
                        )}

                        {isReviewOpen && (
                          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                            <ReviewForm
                              mode="create"
                              reservationId={r._id}
                              hotelName={r.hotelId?.name}
                              onSuccess={() => handleReviewSuccess(r._id)}
                            />
                            <button onClick={() => setOpenReviewId(null)}
                              style={{ marginTop: 10, fontSize: 12, color: "#9ca3af", background: "none", border: "none", cursor: "pointer" }}>
                              Annuler
                            </button>
                          </motion.div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}